import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { indexOf, mergeAll } from "ramda";
import { useFetchHistoricRentability } from "../../../api";
import {
  useAllPortfolios,
  useBenchmarks,
  usePeriod,
  useShowAssets,
} from "../../../store";
import { RentabilityTableRow } from "./RentabilityTableRow/RentabilityTableRow";
import {
  STableContainer,
  SGridRow1,
  SGridRow2,
  SGridRow3,
  STable,
  STableBody,
  STableTitle,
  SHeaderTableRow,
  STableRow,
  SCell,
  SButtonGroup,
  SButton,
  SHeaderCell,
  SFirstColumnHeaderCell,
  SCircularProgress,
} from "./styles";
import { generatePortfolioColor } from "../../../utils";

export const RentabilityTable: React.FC = () => {
  const { t } = useTranslation();
  const { allPortfolios } = useAllPortfolios();
  const [toggle, setToggle] = useState(true);
  const { selectedPeriod } = usePeriod();
  const { isAssetsShowing } = useShowAssets();
  const {
    loading: isLoading,
    data: rentabilityTableData,
    fetch: fetchRentabilityTableData,
  } = useFetchHistoricRentability();
  const { selectedBenchmarks } = useBenchmarks();

  const portfoliosAssets = useMemo(
    () =>
      mergeAll(
        Object.keys(allPortfolios).map(
          (key) => allPortfolios[Number(key)].assets
        )
      ),
    [allPortfolios]
  );

  const handleToggle = (): void => {
    setToggle((previousState) => !previousState);
  };

  useEffect(() => {
    fetchRentabilityTableData(
      allPortfolios,
      selectedBenchmarks,
      selectedPeriod,
      toggle ? "RETORNO" : "PORCENTAGEM_BENCHMARK_RETORNO"
    );
  }, [allPortfolios, selectedPeriod, toggle, selectedBenchmarks]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <STableContainer>
      <SGridRow1>
        <STableTitle>{t("rentabilityTable.title")}</STableTitle>
      </SGridRow1>
      <SGridRow2>
        {t("rentabilityTable.return")}:
        <SButtonGroup>
          <SButton $isSelected={toggle === true} onClick={handleToggle}>
            {t("rentabilityTable.return")}
          </SButton>
          <SButton $isSelected={toggle === false} onClick={handleToggle}>
            % CDI
          </SButton>
        </SButtonGroup>
      </SGridRow2>
      <SGridRow3>
        <STable>
          <STableBody>
            <SHeaderTableRow key="rentability-header">
              <SFirstColumnHeaderCell>{t("portfolio")}</SFirstColumnHeaderCell>
              <SHeaderCell>{t("rentabilityTable.month")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.yearToDate")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.sixMonths")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.year")}</SHeaderCell>
              <SHeaderCell>{t("periodButton.title")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.participation")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.initialBalance")}</SHeaderCell>
              <SHeaderCell>{t("rentabilityTable.grossBalance")}</SHeaderCell>
            </SHeaderTableRow>
            {isLoading ? (
              <STableRow key="rentability-progress-key">
                <SCell colSpan={9}>
                  <SCircularProgress />
                </SCell>
              </STableRow>
            ) : (
              <React.Fragment key="rentability-assets-key">
                {Object.keys(allPortfolios).map(
                  (portfolioKey) =>
                    rentabilityTableData?.[portfolioKey] && (
                      <React.Fragment key={`rentability-${portfolioKey}`}>
                        <RentabilityTableRow
                          key={portfolioKey}
                          asset={{
                            ...rentabilityTableData[portfolioKey],
                            key: portfolioKey,
                            color: generatePortfolioColor(
                              Number(portfolioKey),
                              allPortfolios
                            ),
                          }}
                        />
                        {isAssetsShowing &&
                          Object.keys(
                            rentabilityTableData[portfolioKey].assets
                          ).map((assetKey) => (
                            <RentabilityTableRow
                              key={`rentability-${assetKey}`}
                              indented
                              asset={{
                                ...rentabilityTableData[portfolioKey].assets[
                                  assetKey
                                ],

                                key: assetKey,
                                color: generatePortfolioColor(
                                  indexOf(
                                    assetKey,
                                    Object.keys(portfoliosAssets)
                                  ) + Object.keys(allPortfolios).length,
                                  allPortfolios
                                ),
                              }}
                            />
                          ))}
                      </React.Fragment>
                    )
                )}
                {Object.keys(selectedBenchmarks).map(
                  (benchmarkKey) =>
                    rentabilityTableData?.[benchmarkKey] && (
                      <RentabilityTableRow
                        key={`rentability-${benchmarkKey}`}
                        asset={{
                          ...rentabilityTableData[benchmarkKey],
                          key: benchmarkKey,
                          color: generatePortfolioColor(
                            indexOf(
                              benchmarkKey,
                              Object.keys(selectedBenchmarks)
                            ) +
                              Object.keys(allPortfolios).length +
                              Object.keys(portfoliosAssets).length,
                            allPortfolios
                          ),
                        }}
                      />
                    )
                )}
              </React.Fragment>
            )}
          </STableBody>
        </STable>
      </SGridRow3>
    </STableContainer>
  );
};
